// FLOW-11 (task 5.1, S11, G18) — o cURL vira um passo `http` do flow, pelo mapeamento da SPEC
// (S11, "Importar cURL"). A leitura é a do módulo compartilhado (`helper/curl/lerCurl.js`); aqui só
// se decide o que vira o quê.
//
// A regra que manda (R22): **credencial nunca vira literal**. O valor do `Authorization`, do
// `Cookie`, do `-u` e de todo header, query ou campo de corpo cujo nome casa com os nomes sensíveis
// do compilador (`_NOME_SENSIVEL`, que a Task 1.0 publica no schema como `x-nome-sensivel`) vira
// `{{ secret('<nome>') }}`, com o esquema fora da fórmula (`Bearer {{ secret('crm_exemplo_token') }}`).
// O valor sai à parte, em `credenciais`, para a tela oferecer "Guardar como segredo"; ele nunca
// entra no passo, e por isso nunca entra no YAML nem no desfazer.
import { lerCurl, partirUrl } from 'dashboard/helper/curl/lerCurl';

// O `id` que o compilador aceita (o mesmo de `documentoYaml.js`, que importa este módulo).
const ID_VALIDO = /^[a-z][a-z0-9_]*$/;

// A mesma lista do compilador (`compilador.py`, `_NOME_SENSIVEL`), para o editor que ainda não tem o
// schema com `x-nome-sensivel`. Um teste confere que é a mesma regex do compilador.
export const NOME_SENSIVEL_PADRAO =
  /(authorization|cookie|token|secret|senha|password|passwd|api[-_]?key|apikey|^key$|client[-_]?secret|access[-_]?key)/i;

// A regex do schema (`x-nome-sensivel`, na raiz), quando ele já a traz; senão, a de cima. O L1 a
// publicou como `{pattern, flags}`, que é como o compilador a lê (`_REGRA_SENSIVEL`); o texto solto
// também vale.
export function nomeSensivelDoSchema(schema) {
  const regra = schema?.['x-nome-sensivel'];
  const fonte = typeof regra === 'string' ? regra : regra?.pattern;
  if (typeof fonte !== 'string' || !fonte) return NOME_SENSIVEL_PADRAO;
  const flags = typeof regra?.flags === 'string' ? regra.flags : 'i';
  try {
    return new RegExp(
      fonte.replace(/^\(\?i\)/, ''),
      flags.includes('i') || fonte.startsWith('(?i)') ? 'i' : ''
    );
  } catch {
    return NOME_SENSIVEL_PADRAO;
  }
}

// Os headers que o motor monta sozinho e que um cURL copiado sempre traz.
const HEADERS_DO_MOTOR = new Set([
  'content-length',
  'host',
  'connection',
  'accept-encoding',
]);
// Os domínios de segundo nível que são "de topo" para o nome do segredo (`exemplo.com.br` é o
// `exemplo`).
const SUFIXOS_DE_DOIS = new Set([
  'com.br',
  'net.br',
  'org.br',
  'gov.br',
  'edu.br',
  'app.br',
  'co.uk',
  'org.uk',
  'com.au',
  'com.ar',
  'com.mx',
  'co.jp',
  'com.pt',
]);
// Os nomes que já são raízes das fórmulas e não servem de `id` de passo.
const RAIZES = new Set([
  'params',
  'ctx',
  'vars',
  'item',
  'iteration',
  'error',
  'now',
  'json',
  'status',
  'headers',
  'contact',
  'conversation',
  'agent',
  'message',
  'account',
  'inbox',
]);

const JSON_PURO = /^application\/json\s*(;.*)?$/i;
const FORMULARIO = /^application\/x-www-form-urlencoded\s*(;.*)?$/i;
const MULTIPART = /^multipart\/form-data/i;

const tirarHeader = (headers, nome) =>
  headers.filter(h => h.nome.toLowerCase() !== nome.toLowerCase());
const headerDe = (headers, nome) =>
  headers.find(h => h.nome.toLowerCase() === nome.toLowerCase());

const minusculoComSublinhado = texto =>
  String(texto)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// O host sem `www`, sem `api` e sem o domínio de topo: `api.crm.exemplo.com` dá `crm_exemplo`.
// FLOW-13 (aceite 7.6): o host sem domínio (`gate4b-duble`, `localhost`, o nome de um serviço da rede)
// é o próprio nome, e não fica vazio (o nome sugerido seria só `token`).
export function baseDoHost(url) {
  let host = '';
  try {
    host = new URL(url).hostname;
  } catch {
    host = (String(url).match(/^[a-z][a-z0-9+.-]*:\/\/([^/:?#]+)/i) || [])[1];
  }
  const rotulos = String(host || '')
    .toLowerCase()
    .split('.')
    .filter(Boolean);
  if (/^\d+$/.test(rotulos[rotulos.length - 1] || '')) {
    return minusculoComSublinhado(rotulos.join('_'));
  }
  if (rotulos.length === 1) return minusculoComSublinhado(rotulos[0]);
  const final = rotulos.slice(-2).join('.');
  const topo = SUFIXOS_DE_DOIS.has(final) ? 2 : 1;
  const meio = rotulos
    .slice(0, Math.max(rotulos.length - topo, 0))
    .filter(r => r !== 'www' && r !== 'api');
  return minusculoComSublinhado(meio.join('_'));
}

// O nome sugerido do segredo: a base do host mais o campo (`authorization` vira `token`, `cookie`
// vira `cookie`, o `-u` vira `basic`; o resto, o próprio nome). Ex.: `crm_exemplo_token`.
export function nomeDoSegredo(url, campo) {
  const SUFIXOS = { authorization: 'token', cookie: 'cookie', basic: 'basic' };
  const sufixo =
    SUFIXOS[String(campo).toLowerCase()] ||
    minusculoComSublinhado(campo) ||
    'segredo';
  const base = baseDoHost(url);
  return base ? `${base}_${sufixo}` : sufixo;
}

const formula = nome => `{{ secret('${nome}') }}`;
const ESQUEMA = /^([A-Za-z][A-Za-z0-9-]*)\s+(\S.*)$/;
// `token=abc`: o nome e o valor. O valor não começa com `=`, para o enchimento do base64 (`QA==`)
// não passar por par e deixar a credencial fora da fórmula.
const PAR = /^([A-Za-z_][A-Za-z0-9_.-]*=)([^=].*)$/;
const temFormula = valor => typeof valor === 'string' && valor.includes('{{');

// O `id` do passo: o último pedaço do caminho da URL (`/v3/objects/contacts` dá `contacts`), ou
// `http`; o que já existe no flow ganha o primeiro sufixo livre (`contacts_2`).
export function idDoPasso(url, ids = new Set()) {
  const { base } = partirUrl(url);
  const caminho = base.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
  const pedacos = caminho.split('/').filter(Boolean);
  const ultimo = pedacos[pedacos.length - 1] || '';
  let raiz = minusculoComSublinhado(ultimo);
  // `{id}` e `:id` são marcadores da doc, e não o nome do recurso.
  if (!ID_VALIDO.test(raiz) || RAIZES.has(raiz) || /[{}:]/.test(ultimo)) {
    raiz = 'http';
  }
  if (!ids.has(raiz)) return raiz;
  let n = 2;
  while (ids.has(`${raiz}_${n}`)) n += 1;
  return `${raiz}_${n}`;
}

// Tradução de um campo que carrega credencial. `{escrito, valor}`: o que vai para o passo e o valor
// que sai à parte. O esquema (`Bearer`, `Basic`) e o par `nome=` ficam fora da fórmula, como o
// compilador aceita (`_ESQUEMA` e `_PAR_DE_EXPRESSAO`).
function semCredencial(valor, nome, { comEsquema = true, comPar = true } = {}) {
  const texto = String(valor ?? '');
  if (comEsquema) {
    const esquema = texto.match(ESQUEMA);
    if (esquema) {
      const par = comPar && esquema[2].match(PAR);
      if (par) {
        return {
          escrito: `${esquema[1]} ${par[1]}${formula(nome)}`,
          valor: par[2],
        };
      }
      return { escrito: `${esquema[1]} ${formula(nome)}`, valor: esquema[2] };
    }
  }
  return { escrito: formula(nome), valor: texto };
}

function base64(texto) {
  const bytes = new TextEncoder().encode(texto);
  let binario = '';
  bytes.forEach(b => {
    binario += String.fromCharCode(b);
  });
  return btoa(binario);
}

const ehJsonEstrutura = texto => {
  try {
    const valor = JSON.parse(texto);
    return valor && typeof valor === 'object' ? { valor } : null;
  } catch {
    return null;
  }
};

const PAR_DE_FORMULARIO = /^[^=&\s]+=[^&]*$/;
const ehFormulario = texto =>
  !!texto && texto.split('&').every(par => PAR_DE_FORMULARIO.test(par));

const lerFormulario = texto =>
  [...new URLSearchParams(texto)].reduce((campos, [nome, valor]) => {
    if (nome in campos) {
      campos[nome] = [].concat(campos[nome], valor);
    } else {
      campos[nome] = valor;
    }
    return campos;
  }, {});

const ARQUIVO = '{{ }}';

// O cURL lido (`lerCurl`) como passo `http`. `{passo, credenciais, avisos}`:
// - `credenciais`: `[{onde, campo, nome, valor}]`, o valor fora do passo;
// - `avisos`: `[{codigo, campo?}]`: `inseguro` (-k), `proxy` (-x), `arquivo` (um `@arquivo` que
//   precisa do arquivo do flow) e `tipo_do_texto` (corpo em texto sem `Content-Type`).
export function passoDoCurl(
  curl,
  { nomeSensivel = NOME_SENSIVEL_PADRAO, ids } = {}
) {
  const sensivel = nome => nomeSensivel.test(String(nome));
  const avisos = curl.avisos.map(codigo => ({ codigo }));
  const credenciais = [];
  const usados = new Set();
  const { base, query: pares } = partirUrl(curl.url);
  const segredo = (onde, campo, valor, opcoes) => {
    let nome = nomeDoSegredo(base, campo);
    if (usados.has(nome)) {
      let n = 2;
      while (usados.has(`${nome}_${n}`)) n += 1;
      nome = `${nome}_${n}`;
    }
    usados.add(nome);
    const { escrito, valor: cru } = semCredencial(valor, nome, opcoes);
    credenciais.push({ onde, campo, nome, valor: cru });
    return escrito;
  };
  // Os escalares de uma estrutura (corpo JSON, formulário) com nome sensível viram `secret()`.
  const limpar = (valor, onde, campo) => {
    if (Array.isArray(valor)) return valor.map(v => limpar(v, onde, campo));
    if (valor && typeof valor === 'object') {
      return Object.fromEntries(
        Object.entries(valor).map(([chave, v]) => [
          chave,
          limpar(v, onde, chave),
        ])
      );
    }
    if (
      campo &&
      sensivel(campo) &&
      valor !== null &&
      valor !== '' &&
      typeof valor !== 'boolean' &&
      !temFormula(valor)
    ) {
      return segredo(onde, campo, String(valor), { comEsquema: false });
    }
    return valor;
  };

  let headers = curl.headers.filter(
    h => !HEADERS_DO_MOTOR.has(h.nome.toLowerCase())
  );
  const query = {};
  pares.forEach(([nome, valor]) => {
    query[nome] = valor;
  });

  // O corpo, pela ordem da SPEC: multipart (-F), arquivo (@), formulário, JSON, texto.
  const corpo = {};
  const dadosDeUrl = curl.dados.filter(d => d.tipo === 'urlencode');
  const dadosSoltos = curl.dados.filter(d => d.tipo !== 'urlencode');
  const textoDosDados = dadosSoltos.map(d => d.valor).join('&');
  const tipoDoConteudo = headerDe(headers, 'content-type')?.valor || '';
  const temCorpo = curl.dados.length > 0 || curl.form.length > 0;

  if (curl.get && curl.dados.length) {
    // `-G`: os dados vão para a query, na ordem do comando.
    curl.dados.forEach(({ valor }) => {
      Object.assign(query, lerFormulario(valor));
    });
  } else if (curl.form.length) {
    corpo.multipart = {};
    curl.form.forEach(({ nome, valor, literal }) => {
      if (!literal && /^[@<]/.test(valor)) {
        corpo.multipart[nome] = ARQUIVO;
        avisos.push({ codigo: 'arquivo', campo: nome });
      } else {
        const semAspas = valor.replace(/^"(.*)"(;.*)?$/, '$1');
        corpo.multipart[nome] = semAspas.replace(/;type=[^;]+$/, '');
      }
    });
    corpo.multipart = limpar(corpo.multipart, 'multipart');
    if (MULTIPART.test(tipoDoConteudo)) {
      headers = tirarHeader(headers, 'content-type');
    }
  } else if (
    dadosSoltos.some(d => d.tipo !== 'raw' && d.valor.startsWith('@'))
  ) {
    corpo.body_file = ARQUIVO;
    avisos.push({ codigo: 'arquivo', campo: 'body_file' });
  } else if (dadosDeUrl.length) {
    const campos = {};
    dadosDeUrl.forEach(({ valor }) => {
      const igual = valor.indexOf('=');
      if (igual > 0) campos[valor.slice(0, igual)] = valor.slice(igual + 1);
    });
    if (textoDosDados) Object.assign(campos, lerFormulario(textoDosDados));
    corpo.form = limpar(campos, 'form');
    if (FORMULARIO.test(tipoDoConteudo)) {
      headers = tirarHeader(headers, 'content-type');
    }
  } else if (textoDosDados) {
    const json = ehJsonEstrutura(textoDosDados);
    if (json) {
      corpo.body = limpar(json.valor, 'body');
      if (JSON_PURO.test(tipoDoConteudo)) {
        headers = tirarHeader(headers, 'content-type');
      }
    } else if (
      ehFormulario(textoDosDados) &&
      (!tipoDoConteudo || FORMULARIO.test(tipoDoConteudo))
    ) {
      corpo.form = limpar(lerFormulario(textoDosDados), 'form');
      headers = tirarHeader(headers, 'content-type');
    } else {
      corpo.body_text = textoDosDados;
      corpo.content_type = tipoDoConteudo || 'text/plain; charset=utf-8';
      if (!tipoDoConteudo) avisos.push({ codigo: 'tipo_do_texto' });
      headers = tirarHeader(headers, 'content-type');
    }
  }

  // As credenciais dos headers e da query. O `authorization` do Chrome (minúsculo) vira
  // `Authorization`, que é onde a caixa do `http` lê o "Token de um segredo da conta" (task 5.8).
  const escritos = {};
  headers.forEach(({ nome, valor }) => {
    const chave =
      nome.toLowerCase() === 'authorization' ? 'Authorization' : nome;
    escritos[chave] =
      sensivel(nome) && !temFormula(valor)
        ? segredo('header', nome, valor)
        : valor;
  });
  if (curl.usuario && !headerDe(headers, 'authorization')) {
    const valor = curl.usuario.includes(':')
      ? curl.usuario
      : `${curl.usuario}:`;
    escritos.Authorization = segredo(
      'basic',
      'basic',
      `Basic ${base64(valor)}`,
      {
        comPar: false,
      }
    );
  }
  if (
    curl.cookie &&
    curl.cookie.includes('=') &&
    !headerDe(headers, 'cookie')
  ) {
    escritos.Cookie = segredo('header', 'cookie', curl.cookie, {
      comEsquema: false,
    });
  }
  Object.keys(query).forEach(nome => {
    if (sensivel(nome) && !temFormula(query[nome])) {
      query[nome] = segredo('query', nome, query[nome], { comEsquema: false });
    }
  });

  let metodo = curl.metodo ? curl.metodo.toUpperCase() : null;
  if (!metodo && curl.head) metodo = 'HEAD';
  if (!metodo) metodo = temCorpo && !curl.get ? 'POST' : 'GET';

  const passo = {
    id: idDoPasso(curl.url, ids),
    type: 'http',
    method: metodo,
    url: base,
  };
  if (Object.keys(query).length) passo.query = query;
  if (Object.keys(escritos).length) passo.headers = escritos;
  Object.assign(passo, corpo);
  if (Number.isFinite(curl.tempoMaximo) && curl.tempoMaximo > 0) {
    passo.timeout = curl.tempoMaximo;
  }
  return { passo, credenciais, avisos };
}

// Do texto colado ao passo. `{ok: true, passo, credenciais, avisos}` ou `{ok: false, erro}`, com o
// erro do leitor (`cmd`, `aspas`, `nao_e_curl`, `sem_url`): a tela mostra um texto só para todos,
// o que pede o formato bash.
export function curlParaPassoHttp(texto, opcoes = {}) {
  const lido = lerCurl(texto);
  if (!lido.ok) return lido;
  return { ok: true, ...passoDoCurl(lido.curl, opcoes) };
}

// -- na caixa do `http` (task 5.8): o cURL troca a requisição do passo, e o resto fica

// Os campos que o Importar cURL preenche ("method, url, query, headers e o corpo"); o que o passo
// tem e o cURL não traz sai, para não sobrar um corpo velho ao lado do novo. O `timeout` só entra
// quando o cURL traz o `--max-time`.
export const CAMPOS_DA_REQUISICAO = [
  'method',
  'url',
  'query',
  'headers',
  'body',
  'form',
  'body_text',
  'content_type',
  'multipart',
  'body_file',
];
// A URL do modelo do passo novo (`documentoYaml.js#modeloDoPasso`): ainda não é do passo.
const URL_DO_MODELO = 'https://api.exemplo.com';

// O passo já tem uma requisição escrita: a caixa pergunta antes de trocar.
export const temRequisicao = passo =>
  CAMPOS_DA_REQUISICAO.some(
    campo =>
      campo !== 'method' &&
      passo?.[campo] !== undefined &&
      !(campo === 'url' && passo.url === URL_DO_MODELO)
  );

// FLOW-13 (aceite 7.6): o nome sugerido de uma credencial se troca no diálogo do cURL. O passo passa a
// ler o segredo pelo nome novo: `secret('de')` vira `secret('para')` em todo texto dos campos da
// requisição. Os nomes são `[a-z0-9_]`, e a aspa e o parêntese de fechar impedem que `crm_token` pegue
// `crm_token_2`.
const chamadaDoSegredo = nome => `secret('${nome}')`;
function trocarSegredo(valor, de, para) {
  if (typeof valor === 'string') {
    return valor.split(chamadaDoSegredo(de)).join(chamadaDoSegredo(para));
  }
  if (Array.isArray(valor)) return valor.map(v => trocarSegredo(v, de, para));
  if (valor && typeof valor === 'object') {
    return Object.fromEntries(
      Object.entries(valor).map(([chave, v]) => [
        chave,
        trocarSegredo(v, de, para),
      ])
    );
  }
  return valor;
}

// As escritas (`[caminho, valor]`) da troca do nome, só nos campos que mudam, numa edição só.
export function escritasDaTrocaDeSegredo(passo, de, para) {
  if (!passo || !de || !para || de === para) return [];
  return CAMPOS_DA_REQUISICAO.filter(campo => passo[campo] !== undefined)
    .map(campo => [campo, trocarSegredo(passo[campo], de, para)])
    .filter(
      ([campo, novo]) => JSON.stringify(novo) !== JSON.stringify(passo[campo])
    )
    .map(([campo, novo]) => [[campo], novo]);
}

// As escritas (`[caminho, valor]`, `undefined` tira o campo) que levam o passo de agora ao do cURL,
// numa edição só.
export function escritasDoCurl(passoAtual, novo) {
  const escritas = CAMPOS_DA_REQUISICAO.filter(
    campo => novo[campo] !== undefined || passoAtual?.[campo] !== undefined
  ).map(campo => [[campo], novo[campo]]);
  if (novo.timeout !== undefined) escritas.push([['timeout'], novo.timeout]);
  return escritas;
}
